import React, { Suspense, lazy, useEffect, useMemo, useState } from "react";
import OnboardingAPI from "../03_Onboarding/helpers/OnboardingAPI";
import {
  POSA_WORDINGS,
  type POSA_FORMAT,
  type POSATypeJavascriptDTO
} from "@tootsweet/model/lottery/POSAType";
import { FullScreenLoader } from "../../components/Loaders/FullScreenLoader/FullScreenLoader";
import TSUtils from "../../utils/helpers/TSUtils";
import { Modal } from "react-bootstrap";
import testsID from "../../utils/TestsID";
import type { PreviewGalleryItem } from "./PreviewGallery";

const PreviewGallery = lazy(() => import("./PreviewGallery"));
import { type POSAPricesJavascriptDTO } from "@tootsweet/model/lottery/POSAPrices";
import { LANGUAGE, LANGUAGE_FR, tt } from "../../utils/i18n/i18n";
import Logo from "../../components/Logo";
import KC_Heading1 from "../../designsystem/typography/KC-Heading/KC_Heading1";
import "./styles.scss";
import { DEFAULT_CURRENCY_SUB_UNITS } from "@tootsweet/model/enums/Currencies";
import { type TranslationsKeys } from "../../utils/i18n/translations/Translations";
import CONFIG from "../../model/TSConfig";
import { POSA_CDN_PREFIX } from "../../utils/constants";

const buildPreviewURL = (posaType: POSATypeJavascriptDTO) => {
  const slug = posaType.shortId.toLowerCase().replaceAll(" ", "");
  return `${CONFIG.cdn_url}/jetpack/${POSA_CDN_PREFIX}/png/${posaType.ratio}_${slug}_${PREVIEW_LANGUAGE}_${POSA_WORDINGS.gift}.png`;
};

const getModelName = (posaType: POSATypeJavascriptDTO) =>
  posaType.nameL?.[PREVIEW_LANGUAGE] ?? posaType.name;

const getFormatLabel = (format: POSA_FORMAT) => {
  const key = FORMAT_TRANSLATION_KEYS[format];
  return key ? tt(key) : format;
};

interface PosaRow {
  format: POSA_FORMAT;
  helloPrintSlug?: string;
  posaTypes: POSATypeJavascriptDTO[];
  prices?: POSAPricesJavascriptDTO;
  representative: POSATypeJavascriptDTO;
}

interface PricesModalData {
  formatLabel: string;
  posaPrices: POSAPricesJavascriptDTO;
}

export default function PublicPOSAPage() {
  const [posaPrices, setPOSAPrices] = useState<POSAPricesJavascriptDTO[]>([]);
  const [posaTypes, setPOSATypes] = useState<POSATypeJavascriptDTO[]>([]);
  const [loading, setIsLoading] = useState(false);
  const [showPricesModal, setShowPricesModal] = useState(false);
  const [pricesModalData, setPricesModalData] = useState<
    PricesModalData | undefined
  >(undefined);

  const [galleryRow, setGalleryRow] = useState<PosaRow | undefined>(undefined);
  const [displayImageModal, setDisplayImageModal] = useState(false);

  const fetch = async () => {
    setIsLoading(true);
    const [posaTypes, posaPrices] = await Promise.all([
      OnboardingAPI.getAllPOSATypes(),
      OnboardingAPI.getAllPOSAPricesWithMarkup()
    ]);
    setPOSATypes(
      posaTypes?.filter((p) => !!p.isSaas && !p.hide && !p.isOnline) ?? []
    );
    setPOSAPrices(posaPrices);
    setIsLoading(false);
  };

  const rows = useMemo<PosaRow[]>(() => {
    const map = new Map<string, PosaRow>();
    posaTypes.forEach((posaType) => {
      const key = `${posaType.format}::${posaType.helloPrintSlug ?? ""}`;
      let row = map.get(key);
      if (!row) {
        row = {
          format: posaType.format,
          helloPrintSlug: posaType.helloPrintSlug,
          posaTypes: [],
          prices: posaPrices.find((p) => p._id === posaType.helloPrintSlug),
          representative: posaType
        };
        map.set(key, row);
      }
      row.posaTypes.push(posaType);
    });
    return Array.from(map.values()).sort((a, b) =>
      getFormatLabel(a.format).localeCompare(getFormatLabel(b.format))
    );
  }, [posaTypes, posaPrices]);

  const galleryItems = useMemo<PreviewGalleryItem[]>(() => {
    if (!galleryRow) {
      return [];
    }
    return galleryRow.posaTypes.map((posaType) => ({
      src: buildPreviewURL(posaType),
      alt: getModelName(posaType),
      caption: getModelName(posaType)
    }));
  }, [galleryRow]);

  const hideModal = () => {
    setShowPricesModal(false);
    setTimeout(() => {
      setPricesModalData(undefined);
    }, 500);
  };

  const hideImageModal = () => {
    setGalleryRow(undefined);
    setDisplayImageModal(false);
  };

  const displayPhoto = (row: PosaRow) => {
    if (!row.posaTypes.length) {
      return;
    }
    setGalleryRow(row);
    setDisplayImageModal(true);
  };

  const displayPricesList = (row: PosaRow) => {
    if (!row.prices) {
      return;
    }
    setPricesModalData({
      formatLabel: getFormatLabel(row.format),
      posaPrices: row.prices
    });
    setShowPricesModal(true);
  };

  useEffect(() => {
    void fetch();
  }, []);

  return (
    <>
      <div className="container">
        <FullScreenLoader loading={loading} />
        <div className="row">
          <div className="col-12 mt-2 d-flex flex-column align-items-center">
            <Logo />

            <KC_Heading1
              className="mt-4 mb-2 text-center"
              text={tt("public_posa_page_heading")}
            />
            <h4 className="text-center">{tt("public_posa_page_subheading")}</h4>
          </div>

          {!!rows.length && (
            <div className="offset-3 col-6 mt-2 d-flex flex-column align-items-center table-container">
              <table id={testsID.POSA_TABLE} className="table table-striped">
                <thead>
                  <th>{tt("public_posa_table_name")}</th>
                  <th>{tt("public_posa_table_price")}</th>
                  <th>{tt("public_posa_table_photos")}</th>
                </thead>
                <tbody>
                  {rows.map((row, index) => {
                    const { representative, prices, format } = row;
                    return (
                      <tr key={`row-${format}-${row.helloPrintSlug ?? index}`}>
                        <td>{getFormatLabel(format)}</td>

                        {representative.hasPriceList && (
                          <td>
                            {prices != null && (
                              <span
                                className="price-link"
                                style={{
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                  color: "blue"
                                }}
                                onClick={() => {
                                  displayPricesList(row);
                                }}
                              >
                                {tt("public_posa_table_price_list")}
                              </span>
                            )}
                          </td>
                        )}

                        {!representative.hasPriceList && (
                          <td>
                            <span className="d-block">
                              {TSUtils.formatAmount(
                                representative.unitPrice ?? 0,
                                DEFAULT_CURRENCY_SUB_UNITS,
                                false,
                                "EUR"
                              )}
                            </span>
                          </td>
                        )}

                        <td>
                          <i
                            className="fas fa-camera"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              displayPhoto(row);
                            }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Modal
        show={displayImageModal}
        onHide={hideImageModal}
        id="preview-modal-dialog"
      >
        <Modal.Body>
          <button
            type="button"
            className="preview-modal-close"
            aria-label={tt("close")}
            onClick={hideImageModal}
          >
            <i className="fas fa-times" />
          </button>
          {displayImageModal && galleryItems.length > 0 && (
            <Suspense fallback={null}>
              <PreviewGallery items={galleryItems} />
            </Suspense>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showPricesModal}
        onHide={hideModal}
        bsSize="small"
        centered={true}
      >
        <Modal.Header>
          <h2 className="text-center">
            {tt("public_posa_modal_title_prefix")} &laquo;{" "}
            {pricesModalData?.formatLabel} &raquo;
          </h2>
        </Modal.Header>
        <Modal.Body
          style={{ height: "80vh", maxHeight: "80vh", overflowY: "auto" }}
        >
          <table
            className="table table-bordered table-striped"
            style={{
              overflowY: "scroll"
            }}
          >
            <thead>
              <tr>
                <th className="text-center">
                  {tt("public_posa_modal_quantity")}
                </th>
                <th className="text-center">{tt("public_posa_modal_price")}</th>
              </tr>
            </thead>
            <tbody>
              {pricesModalData?.posaPrices?.prices &&
                Object.keys(pricesModalData.posaPrices.prices).map(
                  (keyStr: string, index) => {
                    const key = parseInt(keyStr);
                    const val = pricesModalData.posaPrices.prices[key];
                    return (
                      <tr key={`price-${index}`}>
                        <td className="text-center">
                          {TSUtils.numberWithCommas(key)}
                        </td>
                        <td className="text-center">
                          {TSUtils.formatAmount(
                            val,
                            DEFAULT_CURRENCY_SUB_UNITS,
                            false,
                            "EUR"
                          )}
                        </td>
                      </tr>
                    );
                  }
                )}
            </tbody>
          </table>
        </Modal.Body>
      </Modal>
    </>
  );
}

const FORMAT_TRANSLATION_KEYS: Record<POSA_FORMAT, keyof TranslationsKeys> = {
  flyer_a5: "posa_flyer_a5",
  flyer_a6: "posa_flyer_a6",
  chevalet_a5: "posa_item_a5_display",
  chevalet_a6: "posa_item_a6_display",
  poster_a2: "posa_item_poster_a2",
  poster_a3: "posa_item_poster_a3",
  poster_a4: "posa_item_poster_a4",
  stickers10x10: "posa_sticker",
  stickers_dome: "posa_sticker_dome",
  a_long: "posa_format_a_long",
  businesscardsstandard: "posa_item_business_card",
  kakemono: "posa_item_kakemono",
  "online-ig-story": "online_posa_ig_story",
  "online-ig-post": "online_posa_ig_post",
  table_set: "posa_item_placemat",
  paper_menu: "posa_item_paper_menu",
  delivery_bag: "posa_form_delivery_print_on_bag",
  print_on_packaging: "posa_form_support_packaging_imprimables",
  print_on_bag: "posa_item_bag_printing"
};

const PREVIEW_LANGUAGE = LANGUAGE === LANGUAGE_FR ? "fr" : "en";
